/* eslint-disable jsx-a11y/click-events-have-key-events */
import type { ComponentType, ReactNode, SVGProps } from "react"
import {
	IconArrowTurnRightFill18,
	IconCheckFill18,
	IconEnvelopeFill18,
	IconMsgContentFill18,
	IconPenFill18,
	IconShareAllLeftFill18,
} from "nucleo-ui-fill-18"
import { IconChevronDownOutline18 as ChevronDown } from "nucleo-ui-outline-18"

import type { EmailContact } from "@productlane/api"
import type { AiDraftSource } from "@productlane/lib/src/ai"
import { cn } from "@productlane/lib/src/cn"
import { Button, Shortcut, SlackIcon, Tooltip } from "@productlane/ui"
import {
	DropdownMenu,
	DropdownMenuGroup,
	DropdownMenuItem,
} from "@productlane/ui/src/components/internal/dropdown"
import { INBOUND_SURFACE_DARK_BG } from "@productlane/ui/src/components/slack-style-message-surface"

import type { AiDraftBadgeState } from "./ai-draft-badge"
import type { ComposeMode, ComposeModeIconKey } from "./compose-mode"
import type { ReplyType, TabType } from "./types"
import type { FromAddressOption } from "./use-from-address"
import type { Editor } from "@tiptap/react"
import { AiDraftBadge } from "./ai-draft-badge"
import {
	getComposeMode,
	getComposeModeIconKey,
	getComposeModeLabel,
} from "./compose-mode"

function formatCcBcc(cc: EmailContact[], bcc: EmailContact[], sep: string) {
	const ccPart = cc.length > 0 ? `${cc.length} Cc` : ""
	const bccPart = bcc.length > 0 ? `${bcc.length} Bcc` : ""
	return ccPart && bccPart ? `${ccPart}${sep}${bccPart}` : ccPart || bccPart
}

type NucleoIcon = ComponentType<SVGProps<SVGSVGElement>>

const COMPOSE_MODE_ICONS: Record<ComposeModeIconKey, NucleoIcon> = {
	reply: IconEnvelopeFill18,
	chatReply: IconMsgContentFill18,
	slack: SlackIcon,
	replyAll: IconShareAllLeftFill18,
	comment: IconPenFill18,
	forward: IconArrowTurnRightFill18,
}

function getComposeModeIcon(
	mode: ComposeMode,
	replyType: ReplyType,
): NucleoIcon {
	return COMPOSE_MODE_ICONS[getComposeModeIconKey({ mode, replyType })]
}

interface BottomBarHeaderProps {
	activeTab: TabType
	replyType: ReplyType
	isForwardMode: boolean
	composeEditor: Editor | null
	commentEditor: Editor | null
	hasClickedReply: boolean
	showEmailRecipientRow: boolean | undefined
	isCcBccPanelOpen: boolean
	isReplyAllMode: boolean
	onReplyAll: () => void
	onSelectPlainReply: () => void
	ccRecipients: EmailContact[]
	bccRecipients: EmailContact[]
	contactEmail: string | undefined | null
	aiDraftBadgeState: AiDraftBadgeState | null
	aiDraftSources: readonly AiDraftSource[] | null | undefined
	onApplyAiDraft: () => void
	onDeleteAiDraft: () => void
	onRegenerateAiDraft: () => void
	onAdjustAiDraft: (instruction: string) => void
	onOpenAiSettings: () => void
	switchTab: (tab: TabType) => void
	setIsForwardMode: (v: boolean) => void
	setForwardRecipients: (v: EmailContact[]) => void
	setHasClickedReply: (v: boolean) => void
	handleEmailContentPopulation: () => void
	toggleCcBccPanel: () => void
	fromAddresses: FromAddressOption[]
	fromAddress: FromAddressOption | null
	onSelectFromAddress: (id: string) => void
	showFromPicker: boolean
	/** A phone row has no room for the sender; the composer shows it below. */
	compact: boolean
}

export function BottomBarHeader({
	activeTab,
	replyType,
	isForwardMode,
	composeEditor,
	commentEditor,
	hasClickedReply,
	showEmailRecipientRow,
	isCcBccPanelOpen,
	isReplyAllMode,
	onReplyAll,
	onSelectPlainReply,
	ccRecipients,
	bccRecipients,
	contactEmail,
	aiDraftBadgeState,
	aiDraftSources,
	onApplyAiDraft,
	onDeleteAiDraft,
	onRegenerateAiDraft,
	onAdjustAiDraft,
	onOpenAiSettings,
	switchTab,
	setIsForwardMode,
	setForwardRecipients,
	setHasClickedReply,
	handleEmailContentPopulation,
	toggleCcBccPanel,
	fromAddresses,
	fromAddress,
	onSelectFromAddress,
	showFromPicker,
	compact,
}: BottomBarHeaderProps) {
	const hasCcBcc = ccRecipients.length > 0 || bccRecipients.length > 0
	const ccBccTooltipLabel = hasCcBcc
		? `Cc and Bcc (${formatCcBcc(ccRecipients, bccRecipients, ", ")})`
		: "Cc and Bcc"
	const ccBccToggleAriaLabel = hasCcBcc
		? `${formatCcBcc(ccRecipients, bccRecipients, " and ")} recipients`
		: "Cc and Bcc"

	const composeMode = getComposeMode({
		activeTab,
		isForwardMode,
		isReplyAllMode,
	})
	const composeModeLabel = getComposeModeLabel({
		mode: composeMode,
		replyType,
	})
	const ComposeModeIcon = getComposeModeIcon(composeMode, replyType)

	const selectReply = () => {
		if (activeTab !== "reply") {
			switchTab("reply")
		}
		setIsForwardMode(false)
		onSelectPlainReply()
		if (!composeEditor || composeEditor.isDestroyed) return
		if (replyType === "email" && !hasClickedReply) {
			handleEmailContentPopulation()
			setHasClickedReply(true)
		}
		composeEditor.commands.focus("end", { scrollIntoView: false })
	}

	// switchTab owns the focus, which has to wait for the tab swap to render.
	const selectComment = () => {
		switchTab("comment")
	}

	const selectForward = () => {
		switchTab("reply")
		setIsForwardMode(true)
		setForwardRecipients([])
		setHasClickedReply(false)
	}

	return (
		<div
			className={cn(
				"sticky top-0 z-10 -mt-4 flex min-h-7 w-full min-w-0 cursor-text flex-row items-center justify-between overflow-visible transition-colors duration-200 ease-out",
				activeTab === "comment"
					? "bg-[#FFFBF2] dark:bg-[#221D12]"
					: cn("bg-white", INBOUND_SURFACE_DARK_BG),
			)}
			role="button"
			tabIndex={0}
			onClick={(e) => {
				if (e.target !== e.currentTarget) return
				if (activeTab === "reply" && composeEditor) {
					if (replyType === "email" && !hasClickedReply) {
						handleEmailContentPopulation()
						setHasClickedReply(true)
					}
					composeEditor.commands.focus("end", {
						scrollIntoView: false,
					})
				} else if (activeTab === "comment" && commentEditor) {
					commentEditor.commands.focus("end", {
						scrollIntoView: false,
					})
				}
			}}
		>
			<DropdownMenu
				modal={false}
				contentProps={{ side: "top", align: "start", sideOffset: 6 }}
				className="w-52"
				trigger={
					<button
						type="button"
						aria-label={`Compose mode: ${composeModeLabel}`}
						className="group -ml-0.5 -mt-0.5 inline-flex h-7 cursor-default items-center gap-1.5 rounded-md px-1.5 text-sm font-medium text-primary outline-none transition-colors duration-150 ease-out hover:bg-gray-150 focus-visible:ring-2 focus-visible:ring-gray-500/40 data-[state=open]:bg-gray-150 dark:hover:bg-gray-750 dark:focus-visible:ring-gray-400/50 dark:data-[state=open]:bg-gray-750"
					>
						<ComposeModeIcon className="h-3.5 w-3.5 shrink-0" />
						<span>{composeModeLabel}</span>
						<ChevronDown
							strokeWidth={1.75}
							className="h-3 w-3 shrink-0 text-secondary transition-transform duration-150 ease-out group-data-[state=open]:rotate-180"
							aria-hidden
						/>
					</button>
				}
			>
				<DropdownMenuGroup>
					<ComposeModeMenuItem
						mode="reply"
						replyType={replyType}
						label={getComposeModeLabel({ mode: "reply", replyType })}
						activeMode={composeMode}
						shortcut={<Shortcut>R</Shortcut>}
						onSelect={selectReply}
					/>
					{replyType === "email" && (
						<ComposeModeMenuItem
							mode="replyAll"
							replyType={replyType}
							label="Reply all"
							activeMode={composeMode}
							shortcut={
								<span className="flex items-center gap-0.5">
									<Shortcut>⇧</Shortcut>
									<Shortcut>R</Shortcut>
								</span>
							}
							onSelect={onReplyAll}
						/>
					)}
					<ComposeModeMenuItem
						mode="comment"
						replyType={replyType}
						label="Comment"
						activeMode={composeMode}
						shortcut={<Shortcut>N</Shortcut>}
						onSelect={selectComment}
					/>
					{replyType === "email" && (
						<ComposeModeMenuItem
							mode="forward"
							replyType={replyType}
							label="Forward"
							activeMode={composeMode}
							shortcut={<Shortcut>W</Shortcut>}
							onSelect={selectForward}
						/>
					)}
				</DropdownMenuGroup>
			</DropdownMenu>

			{showFromPicker &&
				!compact &&
				activeTab === "reply" &&
				replyType === "email" && (
					<FromAddressPicker
						fromAddresses={fromAddresses}
						fromAddress={fromAddress}
						onSelectFromAddress={onSelectFromAddress}
						className="ml-1 min-w-32"
					/>
				)}

			<div className="flex min-w-0 flex-1 items-center justify-end gap-2 pl-2">
				<div className="flex min-w-0 items-center gap-2 overflow-hidden">
					{activeTab === "reply" &&
						(replyType === "email" || replyType === "live-chat") && (
							<div
								className={cn(
									"flex flex-wrap items-center gap-2 transition-opacity duration-75 ease-out",
									showEmailRecipientRow
										? "opacity-100"
										: "pointer-events-none opacity-0",
								)}
								aria-hidden={!showEmailRecipientRow}
							>
								<div className="flex min-w-0 max-w-full items-center gap-1">
									{!isForwardMode && contactEmail ? (
										<button
											type="button"
											onMouseDown={(e) => e.preventDefault()}
											onClick={toggleCcBccPanel}
											aria-expanded={isCcBccPanelOpen}
											aria-label={ccBccToggleAriaLabel}
											className="m-0 min-w-0 max-w-full cursor-default truncate border-0 bg-transparent p-0 text-left text-sm font-normal text-gray-500 opacity-90 outline-none transition-opacity duration-150 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-500/40 dark:text-gray-400 dark:focus-visible:ring-gray-400/50"
										>
											{contactEmail}
										</button>
									) : null}
									{ccRecipients.length + bccRecipients.length > 0 ? (
										<span className="shrink-0 text-sm text-gray-500 dark:text-gray-400">
											and {ccRecipients.length + bccRecipients.length} other
											{ccRecipients.length > 1 || bccRecipients.length > 1
												? "s"
												: ""}
										</span>
									) : null}
									<Tooltip label={ccBccTooltipLabel} shouldWrapChildren>
										<Button
											type="button"
											variant="ghost"
											size="sm"
											onMouseDown={(e) => e.preventDefault()}
											onClick={toggleCcBccPanel}
											aria-expanded={isCcBccPanelOpen}
											aria-label={ccBccToggleAriaLabel}
											className="h-7 w-7 shrink-0 cursor-default p-0 text-gray-500 dark:text-gray-400"
										>
											<ChevronDown
												strokeWidth={1.75}
												className={cn(
													"h-3.5 w-3.5 origin-center transition-transform duration-200 ease-out will-change-transform [transform-box:fill-box]",
													isCcBccPanelOpen && "rotate-180",
												)}
												aria-hidden
											/>
										</Button>
									</Tooltip>
								</div>
							</div>
						)}
				</div>
				{activeTab === "reply" && aiDraftBadgeState !== null && (
					<AiDraftBadge
						state={aiDraftBadgeState}
						sources={aiDraftSources}
						onApply={
							aiDraftBadgeState === "available" ? onApplyAiDraft : undefined
						}
						onDelete={onDeleteAiDraft}
						onRegenerate={onRegenerateAiDraft}
						onAdjust={onAdjustAiDraft}
						onOpenAiSettings={onOpenAiSettings}
					/>
				)}
			</div>
		</div>
	)
}

export function FromAddressPicker({
	fromAddresses,
	fromAddress,
	onSelectFromAddress,
	className,
}: {
	fromAddresses: FromAddressOption[]
	fromAddress: FromAddressOption | null
	onSelectFromAddress: (id: string) => void
	className?: string
}) {
	return (
		<DropdownMenu
			contentProps={{ align: "start" }}
			trigger={
				<button
					type="button"
					aria-label={`Send from: ${fromAddress?.email ?? "default address"}`}
					className={cn(
						"group inline-flex h-7 min-w-0 cursor-default items-center gap-1.5 rounded-md px-1.5 text-sm font-normal text-secondary outline-none hover:bg-gray-150 focus-visible:ring-2 focus-visible:ring-gray-500/40 data-[state=open]:bg-gray-150 dark:hover:bg-gray-750 dark:focus-visible:ring-gray-400/50 dark:data-[state=open]:bg-gray-750",
						className,
					)}
				>
					<span className="shrink-0 text-tertiary">From</span>
					<span className="truncate">{fromAddress?.email}</span>
					<ChevronDown
						strokeWidth={1.75}
						className="ml-auto h-3 w-3 flex-none text-secondary transition-transform duration-150 ease-out group-data-[state=open]:rotate-180"
						aria-hidden
					/>
				</button>
			}
		>
			<DropdownMenuGroup>
				{fromAddresses.map((address) => (
					<DropdownMenuItem
						key={address.id}
						Icon={IconEnvelopeFill18}
						IconRight={
							address.id === fromAddress?.id ? IconCheckFill18 : undefined
						}
						onSelect={() => onSelectFromAddress(address.id)}
					>
						<span className="truncate">{address.email}</span>
					</DropdownMenuItem>
				))}
			</DropdownMenuGroup>
		</DropdownMenu>
	)
}

function ComposeModeMenuItem({
	mode,
	replyType,
	label,
	activeMode,
	shortcut,
	onSelect,
}: {
	mode: ComposeMode
	replyType: ReplyType
	label: string
	activeMode: ComposeMode
	shortcut: ReactNode
	onSelect: () => void
}) {
	const Icon = getComposeModeIcon(mode, replyType)
	const isActive = mode === activeMode

	return (
		<DropdownMenuItem
			onSelect={onSelect}
			shortcut={shortcut}
			className={cn(isActive && "text-primary")}
		>
			<Icon
				className={cn(
					"mr-2 h-4 w-4 shrink-0",
					isActive ? "text-primary" : "text-secondary",
				)}
			/>
			<span>{label}</span>
			{isActive ? (
				<IconCheckFill18 className="ml-1.5 h-3.5 w-3.5 shrink-0 text-primary" />
			) : null}
		</DropdownMenuItem>
	)
}
